/* eslint-disable react/no-did-mount-set-state */
import React from 'react';
import { graphql, compose } from 'react-apollo';
import gql from 'graphql-tag';
import { toast } from 'react-toastify';
import { Button } from 'reactstrap';
import _orderBy from 'lodash/orderBy';

import AppComponent from './core/AppComponent';

import Loading from './core/Loading';
import CreateResidentForm from './CreateResidentForm';
import UserWithAlerts from './UserWithAlerts';
import FormModal from './core/FormModal';

import '../css/Clients.css';

class Residents extends AppComponent {
  state = {
    openDropdown: -1,
    showModal: false,
    residentToEdit: null,
  };

  createResident = async ({ name, deviceId, roomNumber }) => {
    try {
      await this.props.createResident({
        variables: {
          name,
          deviceId,
          facilityId: this.props.facilityId,
          roomNumber,
        },
      });
      this.toggleModal();
      await this.refetchData();
      toast.success('Created Resident');
    } catch (err) {
      console.error(err);
      toast.error('Error creating Resident. Please try again later.');
    }
  };

  deleteResident = async id => {
    try {
      await this.props.deleteResident({
        variables: {
          id,
        },
      });
      await this.refetchData();
      toast.success('Deleted Resident');
    } catch (err) {
      console.error(err);
      toast.error('Error deleting Resident. Please try again later.');
    }
  };

  updateResident = async ({ id, name, deviceId, roomNumber }) => {
    try {
      await this.props.updateResident({
        variables: {
          id,
          name,
          deviceId,
          roomNumber,
        },
      });
      this.toggleModal();
      this.setState({
        residentToEdit: null,
      });
      await this.refetchData();
      toast.success('Updated Resident');
    } catch (err) {
      console.error(err);
      toast.error('Error updating Resident. Please try again later.');
    }
  };

  refetchData = async () => Promise.all([
    this.props.devicesQuery.refetch(),
    this.props.residentsQuery.refetch(),
  ]);

  handleValidSubmit = async (e, values) => {
    if (this.state.residentToEdit) {
      return this.updateResident(values);
    }
    return this.createResident(values);
  };

  getResidentAlerts = resident => resident.alerts;

  toggleModal = () => {
    this.setState({
      showModal: !this.state.showModal,
    });
  };

  toggleAddModal = () => this.setState({
    showModal: !this.state.showModal,
    residentToEdit: null,
  });

  toggleRow = index => () => {
    let newState = index;
    if (index === this.state.openDropdown) {
      newState = -1;
    }
    this.setState({
      openDropdown: newState,
    });
  };

  getAlerts = (type, alerts) => {
    if (!alerts) {
      return [];
    }
    return alerts.filter(a => a.alertStatus === type);
  };

  handleDeleteResident = id => () => this.deleteResident(id);

  handleEditResident = id => () => {
    const resident = this.props.residentsQuery.facilityResidents.find(r => r.id === id);
    this.setState({
      residentToEdit: resident,
    });
    this.toggleModal();
  };

  render() {
    const { devicesQuery, residentsQuery } = this.props;

    if (devicesQuery.loading || residentsQuery.loading) {
      return (
        <Loading />
      );
    }

    const { facilityDevices = [] } = devicesQuery;
    const { facilityResidents = [] } = residentsQuery;
    const { showModal, openDropdown, residentToEdit } = this.state;
    const isEditing = !!residentToEdit;
    const sortedResidents = _orderBy(facilityResidents, [s => s.name.toLowerCase()], ['asc']);

    return (
      <React.Fragment>

        <div className="text-right">
          <h2 className="nightingale-text float-left">Residents</h2>
          <Button color="info" onClick={this.toggleAddModal}><span className="cloudSense-Clients-title icon-user-plus" /></Button>
        </div>
        <hr />
        <div>
          {
            sortedResidents.length
              ? sortedResidents.map((resident, index) =>
                (<UserWithAlerts
                  key={resident.id}
                  user={resident}
                  isShowing={openDropdown === index}
                  onDropdownClick={this.toggleRow(index)}
                  unclaimedAlerts={this.getAlerts('UNCLAIMED', this.getResidentAlerts(resident))}
                  claimedAlerts={this.getAlerts('CLAIMED', this.getResidentAlerts(resident))}
                  confirmedAlerts={this.getAlerts('CONFIRMED', this.getResidentAlerts(resident))}
                  resolvedAlerts={this.getAlerts('RESOLVED', this.getResidentAlerts(resident))}
                  onDeleteClick={this.handleDeleteResident}
                  onEditClick={this.handleEditResident}
                />))
              : <span className="text-muted font-italic">No Residents. Add one!</span>
          }
        </div>

        <FormModal
          showModal={showModal}
          toggleModal={this.toggleModal}
          modalTitle={isEditing ? 'Edit Resident' : 'Add Resident'}
          modalTitleIcon="icon-user-plus"
          onValidSubmit={this.handleValidSubmit}
        >
          <CreateResidentForm user={residentToEdit} devices={[...facilityDevices]} />
        </FormModal>

      </React.Fragment>
    );
  }
}

const RESIDENTS_QUERY = gql`
  query FacilityResidents($facilityId: String!) {
    facilityResidents(facilityId: $facilityId) {
      id
      name
      roomNumber
      device {
        name
        deviceId
      }
      alerts {
        id
        alertStatus
        createdAt
        claimedAt
        claimedBy {
          name
        }
        confirmedAt
        confirmedBy {
          name
        }
        resolvedAt
        resolvedBy {
          name
        }
        resident {
          name
        }
      }
    }
  }
`;

const DEVICES_QUERY = gql`
    query FacilityDevices($facilityId: String!) {
        facilityDevices(facilityId: $facilityId) {
            id
            deviceId
            resident {
                id
                name
            }
        }
    }
`;

const CREATE_RESIDENT = gql`
mutation ($name: String!, $facilityId: String!, $deviceId: String, $roomNumber: String!) {
  createResident(
    name: $name
    facilityId: $facilityId
    deviceId: $deviceId
    roomNumber: $roomNumber
  ) {
    id
    name
    device {
      deviceId
    }
  }
}
`;

const UPDATE_RESIDENT = gql`
mutation UpdateResident($id: String!, $name: String!, $deviceId: String, $roomNumber: String!) {
  updateResident(
    name: $name
    deviceId: $deviceId
    residentId: $id
    roomNumber: $roomNumber
  ) {
    id
  }
}
`;

const DELETE_RESIDENT = gql`
mutation DeleteResident($id: String!) {
  deleteResident(residentId: $id) {
    id
  }
}
`;

export default compose(
  graphql(RESIDENTS_QUERY, {
    name: 'residentsQuery',
    options: props => ({
      variables: {
        facilityId: props.facilityId,
      },
      pollInterval: 5000,
    }),
  }),
  graphql(DEVICES_QUERY, {
    name: 'devicesQuery',
    options: props => ({
      variables: {
        facilityId: props.facilityId,
      },
    }),
  }),
  graphql(CREATE_RESIDENT, {
    name: 'createResident',
  }),
  graphql(DELETE_RESIDENT, {
    name: 'deleteResident',
  }),
  graphql(UPDATE_RESIDENT, {
    name: 'updateResident',
  }),
)(Residents);



// WEBPACK FOOTER //
// ./src/components/Residents.js